// SuperOps uses Snowflake-style 64-bit numeric IDs (e.g. CompanyId
// 2026649817645965312) that exceed Number.MAX_SAFE_INTEGER
// (9,007,199,254,740,991 - 16 digits). JSON.parse converts every JSON
// number to a JS `number` (an IEEE-754 double), which cannot represent
// integers past that threshold exactly - it silently rounds to the
// nearest representable value. That corrupted value then gets sent back
// verbatim in later requests (e.g. TicketCreate/{psaId}/contacts/v2?
// companyId=...), which the backend rejects as invalid - reported as
// occasional 400s "as [a SuperOps partner] switches between tickets."
// See MSPPROC-17664.
//
// Fix: before parsing, wrap any bare (unquoted) JSON integer of 16+
// digits in quotes, so it round-trips through JSON.parse as an exact
// string instead of a lossy number. Only touches JSON *number* positions -
// immediately after ":" (an object property value), "[" (a leading array
// element), or "," (a later array element) - so digits already inside a
// quoted string value are untouched. (A lookbehind would read cleaner but
// isn't used here since this file also runs in the hosted app, which can
// be opened in older Safari versions that predate lookbehind support.)
// IDs are never used arithmetically in this app (only displayed, compared,
// and passed back as identifiers), so representing an oversized one as a
// string instead of a number is safe everywhere it's consumed.
const LARGE_BARE_INTEGER = /([:,[]\s*)(-?\d{16,})(?=\s*[,}\]])/g;

// A naive raw.replace(LARGE_BARE_INTEGER, ...) over the WHOLE payload also
// matches that same ":<16+ digits>," shape when it occurs inside an ordinary
// quoted string value (e.g. a contact name like "Contact: 2026649817645965312,
// Inc"), inserting stray quotes inside the string and breaking it - JSON.parse
// then throws instead of fixing anything. Fix: only substitute in the text
// OUTSIDE string literals, by walking the string-literal spans first (this
// regex handles \" escapes, so it won't end a string early on an escaped
// quote) and leaving everything inside them untouched.
const STRING_LITERAL = /"(?:[^"\\]|\\.)*"/g;

export function parseJsonPreservingLargeIntegers<T>(raw: string): T {
  let sanitized = '';
  let lastIndex = 0;

  for (const match of raw.matchAll(STRING_LITERAL)) {
    sanitized += raw.slice(lastIndex, match.index).replace(LARGE_BARE_INTEGER, '$1"$2"');
    sanitized += match[0];
    lastIndex = match.index! + match[0].length;
  }
  sanitized += raw.slice(lastIndex).replace(LARGE_BARE_INTEGER, '$1"$2"');

  return JSON.parse(sanitized);
}
